import { QuestionnaireDataTypes } from 'config-types/config/questionnaire';
import { Questionnaire, QuestionnaireItem } from 'fhir/r4b';
import z from 'zod';
import type { FormPlacement } from '../../../helpers/practice-managed-questionnaires';
import { QAndQRResponse } from '../paperwork/paperwork.types';
import {
  GetPatientFormResponsesInputSchema,
  GetStandAlonePaperworkInputSchema,
  PracticeManagedQuestionnaireItemSchema,
  PracticeManagedQuestionnaireSchema,
  PracticeManagedQuestionnaireUpdateStatusSchema,
} from './practice-managed-questionnaire.schema';

export type QuestionnaireItemType = Exclude<
  QuestionnaireItem['type'],
  'question' | 'time' | 'dateTime' | 'quantity' | 'reference' | 'url' | 'attachment' | 'integer'
>;

export const QUESTIONNAIRE_ITEM_TYPES = [
  'string',
  'text',
  'decimal',
  'boolean',
  'display',
  'choice',
  'open-choice',
  'date',
  'group',
  // 'attachment',
  // 'integer',
] as const satisfies readonly QuestionnaireItemType[];

export const OTTEHR_DATA_TYPES = QuestionnaireDataTypes.filter(
  (t) => t !== 'Payment Validation' && t !== 'Medical History'
) as unknown as readonly ['ZIP', 'Email', 'Phone Number', 'DOB', 'Signature', 'Image', 'PDF', 'Call Out', 'SSN'];

export type OttehrDataType = (typeof OTTEHR_DATA_TYPES)[number];

// Which OttehrDataType are valid for each FHIR QuestionnaireItemType
export const DATA_TYPES_BY_ITEM_TYPE: Partial<Record<QuestionnaireItemType, OttehrDataType[]>> = {
  string: ['Phone Number', 'Email', 'ZIP', 'SSN', 'Signature'],
  date: ['DOB'],
  // attachment: ['Image', 'PDF'],
  display: ['Call Out'],
};
export const OTTEHR_INPUT_WIDTHS = ['s', 'm', 'l'] as const;
export type OttehrInputWidth = (typeof OTTEHR_INPUT_WIDTHS)[number];

/** Placements stored as a tag on the form; Visit details is the untagged default. */
export const TAGGED_FORM_PLACEMENTS = ['screening', 'questionnaires'] as const;
export type TaggedFormPlacement = (typeof TAGGED_FORM_PLACEMENTS)[number];

export type PracticeManagedQuestionnaireItem = Omit<QuestionnaireItem, 'item'> &
  z.infer<typeof PracticeManagedQuestionnaireItemSchema> & {
    item?: PracticeManagedQuestionnaireItem[];
  };

export type PracticeManagedQuestionnaire = Questionnaire & z.infer<typeof PracticeManagedQuestionnaireSchema>;

export type PracticeManagedQuestionnaireUpdateStatusData = z.infer<
  typeof PracticeManagedQuestionnaireUpdateStatusSchema
>;

export type PracticeManagedQuestionnaireDTO = {
  id: string;
  title: string;
  status: Questionnaire['status'];
  url: string;
  placement: FormPlacement;
  readOnly: boolean; // questionnaires imported via raw json are read only in the admin portal
};

export type StandaloneFormDTO = Omit<QAndQRResponse, 'questionnaireTitle'> & {
  questionnaireId: string;
  questionnaireTitle: string;
  /** Where the form's answers appear; absent means Visit Details. */
  placement?: FormPlacement;
  /** The form's canonical url, shared by all of its versions. */
  questionnaireUrl?: string;
};

/** A response from one of the patient's visits, for pages that show forms across visits. */
export type PatientFormResponse = StandaloneFormDTO & {
  encounterId: string;
  appointmentId?: string;
  visitDate?: string;
  /** IANA zone of the visit's office, so its date reads as it did there. */
  visitTimezone?: string;
  /** False for forms answered inside the visit's paperwork, which are deleted with the paperwork. */
  deletable: boolean;
};

export type GetPatientFormResponsesInput = z.infer<typeof GetPatientFormResponsesInputSchema>;
export type GetPatientFormResponsesOutput = { responses: PatientFormResponse[] };

// ============= api input / output types ===============

// get practice managed questionnaire detail
export type PracticeManagedQuestionnaireGetInput = {
  questionnaireId: string;
};

// json imported questionnaires are not edited with the questionnaire builder, so they are returned as raw fhir
// (they may contain attributes the builder cannot process)
export type PracticeManagedQuestionnaireGetOutput =
  | {
      readOnly: false;
      practiceManagedQuestionnaire: PracticeManagedQuestionnaire;
    }
  | {
      readOnly: true;
      questionnaire: Questionnaire;
    };

// get practice managed questionnaire list
export type PracticeManagedQuestionnaireListOutput = {
  practiceManagedQuestionnaires: PracticeManagedQuestionnaireDTO[];
};

// update practice managed questionnaire
export type PracticeManagedQuestionnaireUpdateStatus = {
  updateType: 'update-status';
  data: PracticeManagedQuestionnaireUpdateStatusData;
};
export type PracticeManagedQuestionnaireUpdateQuestionnaire = {
  updateType: 'update-questionnaire';
  data: PracticeManagedQuestionnaire;
};
export type PracticeManagedQuestionnaireUpdateInput =
  | PracticeManagedQuestionnaireUpdateStatus
  | PracticeManagedQuestionnaireUpdateQuestionnaire;

// updating a questionnaire's content creates a new resource (a new version), so the
// caller needs the new id back; updating status patches in place, so there is no new id
export type PracticeManagedQuestionnaireUpdateOutput = {
  questionnaireId: string | undefined;
};

// create managed questionnaire
export type PracticeManagedQuestionnaireCreateInput = {
  practiceManagedQuestionnaire: PracticeManagedQuestionnaire;
};
export type PracticeManagedQuestionnaireCreateOutput = {
  questionnaireId: string;
};

// import a raw fhir questionnaire json, sent as the uploaded / pasted text so the zambda does all the validation
export type PracticeManagedQuestionnaireImportJsonInput = {
  questionnaireJson: string;
  questionnaireId?: string; // when questionnaireId is passed, the json is validated against that specific questionnaire
};
export type PracticeManagedQuestionnaireImportJsonOutput = {
  questionnaireId: string;
  version: string;
};

// used for patient app rendering custom, standalone forms
// return is type UCGetPaperworkResponse which is the same as get-paperwork
export type GetStandAlonePaperworkInput = z.infer<typeof GetStandAlonePaperworkInputSchema>;
